import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import DealCard from './components/DealCard';
import DealFilters from './components/DealFilters';
import DealAlerts from './components/DealAlerts';
import PriceHistoryChart from './components/PriceHistoryChart';
import ExpiredDeals from './components/ExpiredDeals';
import DealStats from './components/DealStats';
import productService from '../../services/productService';

const DealTracker = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: 'all',
    discount: 0,
    priceRange: [0, 1000]
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const allProducts = await productService?.getAllProducts();
      // Filter only products with discounts
      const dealsProducts = allProducts?.filter(p => p?.discount > 0);
      setProducts(dealsProducts);
    } catch (error) {
      console.error('Error loading deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredDeals = () => {
    let filtered = [...products];

    if (filters?.category !== 'all') {
      filtered = filtered?.filter(p => p?.category === filters?.category);
    }

    filtered = filtered?.filter(p => p?.discount >= filters?.discount);
    
    filtered = filtered?.filter(
      p => p?.currentPrice >= filters?.priceRange?.[0] && p?.currentPrice <= filters?.priceRange?.[1]
    );

    return filtered?.sort((a, b) => b?.discount - a?.discount);
  };

  const filteredDeals = getFilteredDeals();
  const topDeal = filteredDeals?.[0];

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      category: 'all',
      discount: 0,
      priceRange: [0, 1000]
    });
  };

  const handleSubscribe = (alertData) => {
    console.log('Subscribe to alert:', alertData);
  };

  const handleSaveDeal = (deal) => {
    console.log('Save deal:', deal);
  };

  const handleShareDeal = (deal) => {
    console.log('Share deal:', deal);
  };

  const handleSetAlert = (deal) => {
    console.log('Set alert for expired deal:', deal);
  };

  const dealStats = {
    totalDeals: filteredDeals?.length || 0,
    averageDiscount: filteredDeals?.length > 0 
      ? filteredDeals?.reduce((sum, deal) => sum + (deal?.discount || 0), 0) / filteredDeals?.length 
      : 0,
    maxDiscount: filteredDeals?.length > 0 
      ? Math.max(...filteredDeals?.map(d => d?.discount || 0)) 
      : 0
  };

  const expiredDeals = [];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="bg-gradient-to-r from-primary to-secondary text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold mb-4">Vacuum Deals Tracker</h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Never miss a great deal on vacuum cleaners. Track prices, set alerts, and save big on your next purchase.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-1 space-y-6">
              <DealFilters 
                filters={filters} 
                onFilterChange={handleFilterChange}
                onFiltersChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />
              <DealStats deals={filteredDeals} stats={dealStats} />
              <DealAlerts onSubscribe={handleSubscribe} />
            </div>

            <div className="lg:col-span-3 space-y-8">
              {topDeal && (
                <div className="bg-gradient-to-r from-accent/10 to-primary/10 rounded-xl p-6 border-2 border-accent">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">🔥</span>
                    <h2 className="text-2xl font-bold text-text-primary">Deal of the Day</h2>
                  </div>
                  <DealCard 
                    deal={topDeal} 
                    featured 
                    onSaveDeal={handleSaveDeal}
                    onShareDeal={handleShareDeal}
                  />
                </div>
              )}

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[...Array(4)]?.map((_, i) => (
                    <div key={i} className="animate-pulse bg-white rounded-xl p-6">
                      <div className="aspect-square bg-muted rounded-lg mb-4" />
                      <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                      <div className="h-4 bg-muted rounded w-1/2" />
                    </div>
                  ))}
                </div>
              ) : filteredDeals?.length === 0 ? (
                <div className="bg-white rounded-xl shadow-subtle border border-border p-12 text-center">
                  <p className="text-lg text-muted-foreground">
                    No deals match your current filters
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-text-primary">
                    Active Deals ({filteredDeals?.length})
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredDeals?.map((deal) => (
                      <DealCard 
                        key={deal?.id} 
                        deal={deal} 
                        onSaveDeal={handleSaveDeal}
                        onShareDeal={handleShareDeal}
                      />
                    ))}
                  </div>
                </div>
              )}

              {topDeal && (
                <PriceHistoryChart 
                  product={topDeal} 
                  priceHistory={topDeal?.priceHistory || []}
                  isVisible={true}
                  onClose={() => {}}
                />
              )}
              <ExpiredDeals 
                expiredDeals={expiredDeals}
                onSetAlert={handleSetAlert}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealTracker;